import React, { useState, useEffect, useCallback } from 'react';
import './ThadingyutAnimation.css';
import lantern1 from '../../assets/thadingyut/lantern1.svg';
import lantern2 from '../../assets/thadingyut/lantern2.svg';
import lantern3 from '../../assets/thadingyut/lantern3.svg';
import fireAnimation from '../../assets/animate/20251003-215725.gif';
import { ImageWithFallback } from '../../utils/imageFallback.tsx';
import thadingyutBg from '../../assets/thadingyut/thadingyut-bg.png';

interface ThadingyutAnimationProps {
  isVisible: boolean;
  onClose: () => void;
}

interface FloatingElement {
  id: number;
  type: 'lantern' | 'firework' | 'spark' | 'burst' | 'rocket';
  x: number;
  y: number;
  size: number;
  speed: number;
  image?: string;
  color?: string;
  delay: number;
  direction?: number;
}

// Move static arrays outside component to prevent recreation on every render
const lanternImages = [lantern1, lantern2, lantern3];
const fireworkColors = [
  '#FFD700',
  '#FF6B35',
  '#F7931E',
  '#FFE135',
  '#C5A572',
  '#FF69B4',
  '#00FF7F',
  '#87CEEB',
  '#FF4500',
  '#DA70D6',
];

const ThadingyutAnimation: React.FC<ThadingyutAnimationProps> = ({ isVisible, onClose }) => {
  const [floatingElements, setFloatingElements] = useState<FloatingElement[]>([]);
  const [showMessage, setShowMessage] = useState(false);

  const greetingMessage = `သီတင်းကျွတ် အချိန်အခါသမယမှာ
ဘေးဘယာဝေးကွာပြီး ပျော်ရွှင်အေးချမ်းတဲ့ ဘဝခရီးလမ်းကို လျှောက်လှမ်းနိုင်ကြပါစေလို့
EZ Myanmar မိသားစုကနေ ဆုတောင်းပေးလိုက်ပါတယ်။`;

  // Create floating elements
  const createFloatingElements = useCallback(() => {
    const elements: FloatingElement[] = [];

    // Create more lanterns with variety
    for (let i = 0; i < 12; i++) {
      elements.push({
        id: i,
        type: 'lantern',
        x: Math.random() * window.innerWidth,
        y: window.innerHeight + 100,
        size: 50 + Math.random() * 40,
        speed: 1 + Math.random() * 2,
        image: lanternImages[i % 3],
        delay: i * 300,
        direction: (Math.random() - 0.5) * 2, // Random horizontal drift
      });
    }

    // Create more sparks with different behaviors
    for (let i = 25; i < 45; i++) {
      elements.push({
        id: i,
        type: 'spark',
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: 4 + Math.random() * 8,
        speed: 2 + Math.random() * 4,
        color: fireworkColors[Math.floor(Math.random() * fireworkColors.length)],
        delay: i * 80,
        direction: Math.random() * 360,
      });
    }

    return elements;
  }, []); // Remove dependencies since arrays are now static

  useEffect(() => {
    if (isVisible) {
      setFloatingElements(createFloatingElements());

      // Show message after 1 second
      const messageTimer = setTimeout(() => {
        setShowMessage(true);
      }, 1000);

      // Auto close after 10 seconds (extended for better experience)
      const autoCloseTimer = setTimeout(() => {
        onClose();
      }, 10000);

      return () => {
        clearTimeout(messageTimer);
        clearTimeout(autoCloseTimer);
      };
    } else {
      setFloatingElements([]);
      setShowMessage(false);
    }
  }, [isVisible, onClose, createFloatingElements]);

  if (!isVisible) return null;

  return (
    <div
      className="thadingyut-animation-overlay"
      onClick={onClose}
      style={{ backgroundImage: `url(${thadingyutBg})`, backgroundSize: 'cover' }}
    >
      <div className="thadingyut-animation-container">
        {/* Floating Elements */}
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className={`floating-element floating-${element.type}`}
            style={
              {
                left: `${element.x}px`,
                top: `${element.y}px`,
                width: `${element.size}px`,
                height: `${element.size}px`,
                animationDelay: `${element.delay}ms`,
                animationDuration:
                  element.type === 'lantern'
                    ? '8s'
                    : element.type === 'rocket'
                      ? '2s'
                      : element.type === 'burst'
                        ? '4s'
                        : element.type === 'firework'
                          ? '3s'
                          : '5s',
                '--direction': `${element.direction || 0}deg`,
              } as React.CSSProperties
            }
          >
            {element.type === 'lantern' && element.image && (
              <img src={element.image} alt="Thadingyut Lantern" className="lantern-image" />
            )}
            {element.type === 'firework' && (
              <div className="firework-burst" style={{ backgroundColor: element.color }} />
            )}
          </div>
        ))}

        {/* Greeting Message */}
        {showMessage && (
          <div className="thadingyut-message">
            <div className="message-content">
              <ImageWithFallback
                src={fireAnimation}
                alt={'thadingyut'}
                className="object-cover h-[80px] mx-auto message-title align-center"
              />
              <h2 className="message-title">သီတင်းကျွတ်</h2>
              <p className="message-text">{greetingMessage}</p>
            </div>
          </div>
        )}

        {/* Close button */}
        <button className="close-animation-btn" onClick={onClose}>
          ✕
        </button>
      </div>
    </div>
  );
};

export default ThadingyutAnimation;
